import React, { useEffect, useState } from "react";
import { Share, Plus, X, Download } from "lucide-react";

const DISMISS_KEY = "fourhundred-install-dismissed-v1";

const isIos = () => {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

/**
 * One-time install prompt:
 *  - Android/desktop Chrome: uses native beforeinstallprompt
 *  - iOS Safari: shows manual "Share → Add to Home Screen" walkthrough
 * Dismissed state is stored in localStorage so we don't nag.
 */
export const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosHintOpen, setIosHintOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS doesn't fire beforeinstallprompt — show our manual hint after a short delay.
    if (isIos()) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBip);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setIosHintOpen(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") dismiss();
      } catch (_) {
        /* ignored */
      }
      setDeferredPrompt(null);
    } else if (isIos()) {
      setIosHintOpen(true);
    }
  };

  if (!visible) return null;

  const cardStyle = {
    background: "var(--surface-card)",
    color: "var(--ink-primary)",
    border: "1px solid var(--border-default)",
    boxShadow: "var(--shadow-hover)",
  };

  return (
    <>
      <div
        data-testid="install-banner"
        className="fixed bottom-36 sm:bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-md w-[calc(100%-1.5rem)]"
      >
        <div className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={cardStyle}>
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "var(--brand-accent-soft)", color: "var(--brand-primary)" }}
          >
            <Download className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight" style={{ color: "var(--ink-primary)" }}>Install the app</p>
            <p className="text-xs leading-tight mt-0.5" style={{ color: "var(--ink-tertiary)" }}>Add to your home screen for one-tap access.</p>
          </div>
          <button
            data-testid="install-banner-install"
            onClick={install}
            className="px-3 py-1.5 rounded-md text-xs font-semibold shrink-0"
            style={{ background: "var(--brand-primary)", color: "var(--ink-on-brand)", transition: "background-color var(--duration-base) var(--ease-standard)" }}
          >
            Install
          </button>
          <button
            data-testid="install-banner-dismiss"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="p-1.5 rounded-md shrink-0"
            style={{ color: "var(--ink-tertiary)", transition: "background-color var(--duration-base) var(--ease-standard)" }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {iosHintOpen && (
        <div
          data-testid="install-banner-ios-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={dismiss}
          style={{ background: "rgba(11,18,20,0.55)" }}
        >
          <div
            className="rounded-2xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--surface-card)", boxShadow: "var(--shadow-modal)", border: "1px solid var(--border-default)" }}
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-base font-bold" style={{ color: "var(--ink-primary)" }}>Add to Home Screen</h3>
              <button
                onClick={dismiss}
                aria-label="Close"
                className="p-1"
                style={{ color: "var(--ink-tertiary)" }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="space-y-3 text-sm" style={{ color: "var(--ink-secondary)" }}>
              <li className="flex items-start gap-2">
                <span className="font-bold w-5 shrink-0" style={{ color: "var(--brand-primary)" }}>1.</span>
                <span className="flex-1">
                  Tap the <Share className="inline h-4 w-4 mx-1 -mt-0.5" style={{ color: "var(--brand-primary)" }} /> <strong>Share</strong> button in Safari&apos;s bottom bar.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold w-5 shrink-0" style={{ color: "var(--brand-primary)" }}>2.</span>
                <span className="flex-1">
                  Scroll down and tap <Plus className="inline h-4 w-4 mx-1 -mt-0.5" style={{ color: "var(--brand-primary)" }} /> <strong>Add to Home Screen</strong>.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold w-5 shrink-0" style={{ color: "var(--brand-primary)" }}>3.</span>
                <span className="flex-1">Tap <strong>Add</strong> in the top-right. The 400 Club icon will appear on your home screen.</span>
              </li>
            </ol>
            <button
              data-testid="install-banner-ios-close"
              onClick={dismiss}
              className="mt-4 w-full px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ background: "var(--brand-primary)", color: "var(--ink-on-brand)" }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};
